import React, { useState, useEffect } from 'react';

const LifeSection = ({ isDarkMode = false }) => {
    const [musicBars, setMusicBars] = useState(Array(16).fill(50));
    const [cubeRotation, setCubeRotation] = useState(0);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [paintingProgress, setPaintingProgress] = useState(0);
    const [marketData, setMarketData] = useState([]);

    useEffect(() => {
        const generateData = () => {
            let price = 50;
            return Array(30).fill(0).map(() => {
                price += (Math.random() - 0.5) * 5;
                return {
                    price,
                    volume: Math.random() * 100,
                    timestamp: new Date()
                };
            });
        };
        setMarketData(generateData());
    }, []);

    useEffect(() => {
        const intervals = [
            setInterval(() => {
                setMarketData(prev => {
                    const newPrice = prev[prev.length - 1].price + (Math.random() - 0.5) * 5;
                    return [...prev.slice(1), {
                        price: newPrice,
                        volume: Math.random() * 100,
                        timestamp: new Date()
                    }];
                });
            }, 1000),
            setInterval(() => setCurrentPhotoIndex(prev => (prev + 1) % 3), 3000),
            setInterval(() => setMusicBars(prev => prev.map(() => Math.floor(20 + Math.random() * 60))), 200),
            setInterval(() => setCubeRotation(prev => (prev + 5) % 360), 50),
            setInterval(() => setPaintingProgress(prev => (prev + 1) % 360), 50)
        ];
        return () => intervals.forEach(clearInterval);
    }, []);

    const bgClass = isDarkMode ? 'bg-[#111111] hover:bg-[#1a2030]' : 'bg-gray-100 hover:bg-gray-50';
    const textClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';

    return (
        <div className="mt-12 sm:mt-16 md:mt-24 lg:mt-32">
            {/* Header */}
            <div className="px-4 sm:px-6 md:px-8 lg:px-32">
                <div className="max-w-3xl mb-12 sm:mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-mono tracking-tight mb-8">Life</h2>
                    <p className="font-mono text-sm sm:text-base text-gray-400 leading-relaxed">
                        Beyond my professional work, I'm passionate about exploring different forms of creativity and expression.
                        Here's what keeps me curious and inspired outside the office.
                    </p>
                </div>
            </div>

            {/* Content Grid */}
            <div className="px-4 sm:px-6 md:px-8 lg:px-32">
                <div className="flex flex-nowrap md:grid md:grid-cols-12 gap-3 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0 -mx-4 md:mx-0 px-4 md:px-0">
                    {/* Photography */}
                    <div className="min-w-[85vw] md:min-w-0 md:col-span-8 group">
                        <div className={`${bgClass} rounded-2xl p-4 h-full transform-gpu transition-all duration-500 hover:-translate-y-1`}>
                            <div className="flex flex-col h-full space-y-3">
                                <div className="flex items-start space-x-3">
                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} 
                                        transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                                        <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="1.5">
                                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                            <circle cx="12" cy="13" r="4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-mono text-base mb-1 group-hover:text-cyan-400 transition-colors">Photography</h3>
                                        <p className={`font-mono text-xs ${textClass} tracking-tight`}>
                                            Exploring street photography and urban landscapes.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-3 mt-auto">
                                    {[0, 1, 2].map((index) => (
                                        <div key={index} className={`aspect-square rounded-xl overflow-hidden transition-all duration-500 
                                            ${index === currentPhotoIndex ? 'opacity-100 scale-105' : 'opacity-40 scale-95'}`}>
                                            <div className={`w-full h-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'} 
                                                transform transition-all duration-500 
                                                ${index === currentPhotoIndex ? 'rotate-0' : 'rotate-3'}`}>
                                                <div className={`w-full h-full bg-gradient-to-br 
                                                    ${index === 0 ? 'from-cyan-200 to-blue-500' :
                                                    index === 1 ? 'from-purple-200 to-cyan-500' :
                                                        'from-blue-200 to-purple-500'} opacity-60`} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Abstract Painting */}
                    <div className="min-w-[85vw] md:min-w-0 md:col-span-4 group">
                        <div className={`${bgClass} rounded-2xl p-4 h-full transform-gpu transition-all duration-500 hover:-translate-y-1`}>
                            <div className="flex flex-col h-full space-y-3">
                                <div>
                                    <div className={`p-2 rounded-lg w-fit ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} 
                                        transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 mb-2`}>
                                        <svg className="w-5 h-5 text-rose-400" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="1.5">
                                            <path d="M4 4h16v16H4z" />
                                            <path d="M4 4l8 16l8-16" />
                                        </svg>
                                    </div>

                                    <h3 className="font-mono text-base mb-1 group-hover:text-rose-400 transition-colors">Abstract Painting</h3>
                                    <p className={`font-mono text-xs ${textClass} tracking-tight`}>
                                        Creating with acrylics and mixed media.
                                    </p>
                                </div>

                                <div className="mt-auto">
                                    <svg viewBox="0 0 100 100" className="w-full h-24">
                                        <path
                                            d={`M50,50 ${Array.from({ length: 36 }, (_, i) => {
                                                const angle = (i * 10 * Math.PI) / 180;
                                                const radius = 20 * Math.sin(angle * 3);
                                                return `${50 + radius * Math.cos(angle)},${50 + radius * Math.sin(angle)}`;
                                            }).join(' L ')} Z`}
                                            stroke={isDarkMode ? '#ff6b6b' : '#ff4d4d'}
                                            fill="none"
                                            strokeWidth="0.5"
                                            strokeDasharray="300"
                                            strokeDashoffset={300 - (paintingProgress / 360) * 300}
                                            className="transition-all duration-300"
                                        />
                                        <path
                                            d="M50,50 L50,80"
                                            stroke={isDarkMode ? '#4caf50' : '#388e3c'}
                                            strokeWidth="0.5"
                                            strokeDasharray="30"
                                            strokeDashoffset={30 - (paintingProgress / 360) * 30}
                                            className="transition-all duration-300"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Market Psychology */}
                    <div className="min-w-[85vw] md:min-w-0 md:col-span-6 group">
                        <div className={`${bgClass} rounded-2xl p-4 h-full transform-gpu transition-all duration-500 hover:-translate-y-1`}>
                            <div className="flex flex-col h-full space-y-3">
                                <div className="flex items-start space-x-3">
                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} 
                                        transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                                        <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="1.5">
                                            <path d="M3 3v18h18" />
                                            <path d="M3 12l6-6 4 4 8-8" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-mono text-base mb-1 group-hover:text-green-400 transition-colors">Market Psychology</h3>
                                        <p className={`font-mono text-xs ${textClass} tracking-tight`}>
                                            Studying behavioral economics.
                                        </p>
                                    </div>
                                </div>

                                <div className="h-24 w-full mt-auto">
                                    <svg className="w-full h-full" viewBox="0 0 100 50">
                                        <defs>
                                            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={isDarkMode ? '#22c55e' : '#16a34a'} stopOpacity="0.3" />
                                                <stop offset="100%" stopColor={isDarkMode ? '#22c55e' : '#16a34a'} stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        <path
                                            d={`M0,50 ${marketData.map((d, i) => `L${(i / (marketData.length - 1)) * 100},${50 - (d.price / 100) * 50}`).join(' ')} V50 Z`}
                                            fill="url(#lineGradient)"
                                        />
                                        <path
                                            d={`M${marketData.map((d, i) => `${(i / (marketData.length - 1)) * 100},${50 - (d.price / 100) * 50}`).join(' L')}`}
                                            stroke={isDarkMode ? '#22c55e' : '#16a34a'}
                                            strokeWidth="0.5"
                                            fill="none"
                                        />
                                        {marketData.map((d, i) => (
                                            <rect
                                                key={i}
                                                x={`${(i / (marketData.length - 1)) * 100 - 0.5}`}
                                                y={48}
                                                width="1"
                                                height={`${(d.volume / 100) * 2}`}
                                                fill={isDarkMode ? '#22c55e33' : '#16a34a33'}
                                            />
                                        ))}
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Speedcubing and Music Production */}
                    <div className="flex flex-nowrap md:grid md:grid-cols-2 gap-3 min-w-[170vw] md:min-w-0 md:col-span-6">
                        {/* Speedcubing */}
                        <div className="group">
                            <div className={`${bgClass} rounded-2xl p-4 h-full transform-gpu transition-all duration-500 hover:-translate-y-1`}>
                                <div className="flex flex-col h-full space-y-3">
                                    <div>
                                        <div className={`p-2 rounded-lg w-fit ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} 
                                            transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 mb-2`}>
                                            <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="1.5">
                                                <path d="M4 4h16v16H4z" />
                                                <path d="M4 12h16M12 4v16M8 4v16M16 4v16" />
                                            </svg>
                                        </div>

                                        <h3 className="font-mono text-base mb-1 group-hover:text-yellow-400 transition-colors">Speedcubing</h3>
                                        <p className={`font-mono text-xs ${textClass} tracking-tight`}>
                                            Current 3x3 average: sub-20.
                                        </p>
                                    </div>

                                    <div className="mt-auto relative h-16">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="grid grid-cols-3 gap-1 transform transition-all duration-300"
                                                 style={{ transform: `rotate(${cubeRotation}deg)` }}>
                                                {[...Array(9)].map((_, i) => (
                                                    <div key={i}
                                                         className={`w-2 h-2 sm:w-3 sm:h-3 rounded-sm transform transition-all duration-300
                                                            ${isDarkMode ? 'bg-yellow-400/40' : 'bg-yellow-600/40'}
                                                            hover:bg-yellow-400/60`}
                                                         style={{
                                                             transform: `scale(${1 + Math.sin((cubeRotation + i * 40) * (Math.PI / 180)) * 0.2})`
                                                         }} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Music Production */}
                        <div className="group">
                            <div className={`${bgClass} rounded-2xl p-4 h-full transform-gpu transition-all duration-500 hover:-translate-y-1`}>
                                <div className="flex flex-col h-full space-y-3">
                                    <div>
                                        <div className={`p-2 rounded-lg w-fit ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} 
                                            transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 mb-2`}>
                                            <svg className="w-5 h-5 text-purple-400" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="1.5">
                                                <path d="M9 18V5l12-2v13" />
                                                <circle cx="6" cy="18" r="3" />
                                                <circle cx="18" cy="16" r="3" />
                                            </svg>
                                        </div>

                                        <h3 className="font-mono text-base mb-1 group-hover:text-purple-400 transition-colors">Music Production</h3>
                                        <p className={`font-mono text-xs ${textClass} tracking-tight`}>
                                            Creating ambient electronic music.
                                        </p>
                                    </div>

                                    <div className="mt-auto flex items-center justify-between h-12 px-2">
                                        {musicBars.map((height, i) => (
                                            <div
                                                key={i}
                                                className={`w-0.5 transform transition-all duration-300
                                                    ${isDarkMode ? 'bg-purple-400/30' : 'bg-purple-600/30'}
                                                    group-hover:bg-purple-400/50`}
                                                style={{
                                                    height: `${height}%`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LifeSection;